/**
 * /app/$tenantSlug/payroll/filings — G_FILE statutory filing hub.
 *
 * Converts LOCKED payroll snapshots (Companies Act §128(1)) into the
 * government-validatable upload files: EPFO ECR (#~#), ESIC monthly,
 * TDS 24Q FVU text, and Form-16 Part B. HR-admin only (same server-side
 * RBAC as every /api/v1/payroll/* route). Entity-switcher-aware — every
 * filing is for the ACTIVE legal entity (TAN/PF/ESI are per-entity).
 *
 * Tabbed by kind. Each tab shows a period grid (months for PF/ESI,
 * quarters for 24Q, the FY for Form-16) with a status chip + a Generate
 * CTA. Generate is step-up TOTP gated (the §128 write-once commit).
 */

import { TAX_AND_PF_FILINGS } from "@/lib/payroll-pages";
import { useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
  useLocation,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  FileBadge,
  FileText,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { PeriodWarningsBanner } from "@/components/payroll/period-warnings-banner";
import { GlossaryTerm } from "@/components/shared/glossary-term";
import { GLOSSARY } from "@/lib/glossary";
import { REGISTRATION_ERROR_CODES, REGISTRATION_HASH } from "@/lib/statutory-registration";
import { form16Available } from "@/lib/filing-periods";
import { form138Stopgap } from "@/lib/form138-stopgap";
import {
  Form138StopgapNote,
  TDSDataCellButton,
} from "@/components/payroll/form138-stopgap";
import { filingFallbackName, latestFilingsByPeriod } from "@/lib/filing-challans";
import { extensionForType } from "@/lib/download-filename";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toApiError } from "@/lib/api";
import { useFilings, useCreateFiling, useRefreshAfterFilingDownload } from "@/hooks/use-filings";
import {
  downloadFilingFile,
  triggerDownload,
  FILING_KIND_LABELS,
} from "@/lib/api/filings";
import type { FilingKind, FilingRun } from "@/lib/api/filings";
import type { UANMissingEmployee } from "@/types/payroll";
import { uanMissingFromError } from "@/lib/payroll-errors";
import { UANMissingAlert } from "@/components/payroll/employee-issue-alerts";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/filings",
)({
  component: FilingsRoute,
});

const KINDS: FilingKind[] = ["ecr", "esi_monthly", "tds_24q_fvu", "form16"];

// Maps each filing kind to its canonical glossary key for the inline explainer.
const KIND_TERMS: Record<FilingKind, keyof typeof GLOSSARY> = {
  ecr: "ECR",
  esi_monthly: "ESICReturn",
  tds_24q_fvu: "Form24Q",
  form16: "Form16",
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function FilingsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [year, setYear] = useState(new Date().getFullYear());
  // /payroll/filings/{id} (the filing detail: 24Q challans, Form 16
  // overrides) is a CHILD of this route — render it, not the hub (2026-10
  // browser QA payroll #3: without an Outlet the detail never rendered).
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;

  return (
    <PayrollModuleShell
      title={TAX_AND_PF_FILINGS}
      description="Generate the government-validatable PF (ECR), ESI, TDS 24Q and Form-16 files from your locked payroll. Validate each file in the official utility (FVU / EPFO / ESIC) before uploading."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/settings/entities"
              params={{ tenantSlug }}
              hash={REGISTRATION_HASH}
              data-testid="filings-registration-link"
            >
              <FileBadge aria-hidden className="h-4 w-4" />
              TAN, PF and ESIC Codes
            </Link>
          </Button>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back to Payroll Runs
            </Link>
          </Button>
        </div>
      }
    >
      {/* Wave 2 — months with two locked full runs are flagged; ECR / ESIC
          / 24Q / Form 16 use the latest run of each month (plus its
          correction runs) only. */}
      <div className="mb-4">
        <PeriodWarningsBanner tenantSlug={tenantSlug} />
      </div>
      <div className="mb-4 flex items-end gap-3">
        <div>
          <Label htmlFor="filing-year">Year</Label>
          <Input
            id="filing-year"
            type="number"
            className="w-28"
            value={year}
            min={2017}
            max={2100}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
          />
        </div>
      </div>

      <Tabs defaultValue="ecr">
        <TabsList className="h-auto flex-wrap justify-start">
          {/* The glossary help is a button, so it lives in each tab's card
              title — inside the tab trigger (itself a button) it nested
              <button> in <button> (2026-10 re-test payroll #9). */}
          {KINDS.map((k) => (
            <TabsTrigger key={k} value={k}>
              {FILING_KIND_LABELS[k]}
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k} value={k}>
            <KindTab kind={k} year={year} tenantSlug={tenantSlug} />
          </TabsContent>
        ))}
      </Tabs>
    </PayrollModuleShell>
  );
}

function KindTab({
  kind,
  year,
  tenantSlug,
}: Readonly<{ kind: FilingKind; year: number; tenantSlug: string }>) {
  const q = useFilings(kind, year);
  // The latest revision of each period (a revised filing supersedes the
  // downloaded one — re-test payroll #7).
  const byIndex = latestFilingsByPeriod(q.data?.items ?? []);

  // Period set per kind.
  let periods: { index: number; label: string }[];
  if (kind === "ecr" || kind === "esi_monthly") {
    periods = MONTH_NAMES.map((m, i) => ({ index: i + 1, label: m }));
  } else if (kind === "tds_24q_fvu") {
    periods = [1, 2, 3, 4].map((qq) => ({ index: qq, label: `Q${qq}` }));
  } else {
    periods = [{ index: 0, label: `FY ${year}-${(year + 1) % 100}` }];
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5 text-base">
          {FILING_KIND_LABELS[kind]}
          <GlossaryTerm term={KIND_TERMS[kind]} />
          <span>— {year}</span>
        </CardTitle>
        <CardDescription>{kindHint(kind, year)}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Tax year 2026-27: Form 138, not 24Q — hand the CA the data
            (Form 138 Q2 stopgap, income-tax-act-2025-forms T0). */}
        {form138Stopgap(kind, year) && <Form138StopgapNote year={year} />}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {periods.map((p) => (
            <PeriodCell
              key={p.index}
              kind={kind}
              year={year}
              index={p.index}
              label={p.label}
              filing={byIndex.get(p.index)}
              tenantSlug={tenantSlug}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PeriodCell({
  kind,
  year,
  index,
  label,
  filing,
  tenantSlug,
}: Readonly<{
  kind: FilingKind;
  year: number;
  index: number;
  label: string;
  filing?: FilingRun;
  tenantSlug: string;
}>) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const createMut = useCreateFiling();
  const refreshAfterDownload = useRefreshAfterFilingDownload();
  const [downloading, setDownloading] = useState(false);
  // A failed Generate must leave a PERSISTENT trace, not just a 4-second
  // toast — "click, nothing happens, month still says Not generated" read
  // as a dead button in the 2026-07-11 UX audit (red #3).
  const [genError, setGenError] = useState<string | null>(null);
  // F199: an ECR refused for PF members without a UAN names each of them.
  const [uanMissing, setUANMissing] = useState<UANMissingEmployee[] | null>(null);

  async function onGenerate() {
    setGenError(null);
    setUANMissing(null);
    try {
      const created = await createMut.mutateAsync({
        kind,
        period_year: year,
        period_index: index,
      });
      toast.success("Filing generated. Download it and validate in the official utility before uploading.");
      void navigate({
        to: "/app/$tenantSlug/payroll/filings/$id",
        params: { tenantSlug, id: String(created.id) },
      });
    } catch (err) {
      const e = toApiError(err);
      if (e.error === "totp_enrollment_required") {
        toast.error("Enrol in two-factor authentication to generate filings.");
        void navigate({ to: "/2fa-setup", search: { required: 1, next: pathname } });
        return;
      }
      if (e.error === "totp_required") {
        toast.error("Two-factor verification required to generate the filing.");
        void navigate({ to: "/2fa-verify", search: { next: pathname } });
        return;
      }
      // Establishment-registration gaps are fixed in workspace settings —
      // say so instead of leaving the admin to hunt for the field.
      const needsEntitySetup =
        e.error === "pf_code_required" ||
        e.error === "esi_code_required" ||
        e.error === "tan_required";
      // toApiError's humaniser ends the server text with a full stop — drop
      // it before the suffix so it doesn't read "…under it). — add it…".
      const msg = needsEntitySetup
        ? `${e.message.replace(/\.$/, "")} — add it under Settings → Entities, then retry.`
        : e.message;
      const missing = uanMissingFromError(err);
      if (missing) {
        setUANMissing(missing);
        return;
      }
      setGenError(msg);
      toast.error(msg);
    }
  }

  async function onDownload() {
    if (!filing) return;
    setDownloading(true);
    try {
      const blob = await downloadFilingFile(filing.id, { download: true });
      // The server's name (an ESIC sheet is a .csv — it used to be saved as
      // .txt, re-test payroll #5); the fallback keeps the response's type.
      triggerDownload(blob, filingFallbackName(filing, extensionForType(blob.type)));
      toast.success(`${FILING_KIND_LABELS[kind]} file downloaded.`);
      // The first download freezes it ("Generated" → "Downloaded").
      if (!filing.frozen) void refreshAfterDownload();
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  // The last Generate refusal (per cell): a missing TAN / PF / ESIC code is
  // fixed on the entity's statutory registration — link straight to it.
  const lastError = createMut.error ? toApiError(createMut.error) : null;
  const needsRegistration = !!lastError && REGISTRATION_ERROR_CODES.has(lastError.error);
  // Form 16 covers the whole year — not offered before its March payroll.
  const form16Early = kind === "form16" && !form16Available(year, new Date());
  // Tax year 2026-27 quarterly TDS is Form 138: no 24Q Generate, only the
  // quarter's TDS data for the CA (Form 138 Q2 stopgap).
  const stopgap = form138Stopgap(kind, year);

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {filing ? (
          <Badge variant={filing.frozen ? "secondary" : "outline"}>
            {filing.frozen ? "Downloaded" : "Generated"}
            {filing.revision_no > 0 ? ` · Rev ${filing.revision_no}` : ""}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Not Generated
          </Badge>
        )}
      </div>
      {filing ? (
        <div className="flex flex-col gap-1.5">
          <Link
            to="/app/$tenantSlug/payroll/filings/$id"
            params={{ tenantSlug, id: String(filing.id) }}
            className="inline-flex items-center text-xs text-primary hover:underline pointer-coarse:min-h-11"
          >
            <FileText className="mr-1 inline h-3 w-3" />
            View detail
          </Link>
          {kind !== "form16" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Download className="h-3 w-3" />
              )}
              Download
            </Button>
          )}
        </div>
      ) : stopgap ? (
        <TDSDataCellButton year={year} quarter={index} />
      ) : (
        <>
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-full gap-1 text-xs"
            onClick={onGenerate}
            disabled={createMut.isPending || form16Early}
          >
            {createMut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ShieldCheck className="h-3 w-3" />
            )}
            Generate
          </Button>
          {form16Early && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="filings-form16-early">
              Form 16 covers the whole year — generate it once the March {year + 1} payroll is locked.
            </p>
          )}
          {needsRegistration && (
            <Link
              to="/app/$tenantSlug/settings/entities"
              params={{ tenantSlug }}
              hash={REGISTRATION_HASH}
              className="mt-2 block text-xs font-medium text-primary underline-offset-4 hover:underline"
              data-testid="filings-add-registration"
            >
              Add it in the statutory registration
            </Link>
          )}
          {genError && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {genError}
            </p>
          )}
          {uanMissing && (
            <div className="mt-2">
              <UANMissingAlert employees={uanMissing} tenantSlug={tenantSlug} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function kindHint(kind: FilingKind, year: number): string {
  switch (kind) {
    case "ecr":
      return "EPFO ECR (#~#) — validate by uploading on the EPFO Unified Portal. Requires the entity's PF establishment code.";
    case "esi_monthly":
      return "ESIC monthly contribution sheet — validate via the ESIC employer portal bulk upload. Requires the entity's ESI employer code.";
    case "tds_24q_fvu":
      return `TDS Form 24Q FVU input text — run through the Protean RPU + FVU before filing.${
        // TY 2026-27 is a hand-to-your-CA stopgap (Form 138) — no TAN/challan prerequisite.
        form138Stopgap(kind, year)
          ? ""
          : " Requires TAN + at least one TDS challan for the quarter."
      }`;
    case "form16":
      return "Form 16 Part B (per employee). Part A comes from TRACES after the 24Q is processed; merge before issuing. Requires TAN.";
  }
}
